import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import api from '../services/api';
import { CategoryWithSubcategories, OrderProduct } from '../types/order';
import StaffOrderAddProductWizard, {
  StaffOrderCartLinePayload,
  StaffOrderWizardCompleteOptions,
  isVoiceBusinessProduct,
  isVoiceProduct,
} from './StaffOrderAddProductWizard';
import { StaffOrderFieldValues } from './StaffOrderProductFieldsForm';
import {
  cartHasLineServiceAddressRequirement,
  deriveOrderLevelServiceAddressFromCart,
  validateCartInstanceServiceAddresses,
} from '../constants/staffOrderServiceAddress';

export type StaffNewOrderDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Prefilled CRM account UUID (e.g. from Account → Orders). */
  defaultAccountId?: string;
  /** When true, the account field is read-only. */
  lockAccount?: boolean;
  /** Pre-fill the service address field (e.g. navigated from SQ tool). */
  profileServiceAddress?: string;
};

type CartRow = {
  id: string;
  product: OrderProduct;
  instances: StaffOrderFieldValues[];
  portingFiles?: (File | null)[];
  skipAddressValidation?: boolean;
};

const emptyForm = () => ({
  customer_type: 'residential' as 'residential' | 'business',
  first_name: '',
  last_name: '',
  email: '',
  mobile_number: '',
  service_address: '',
  billing_address: '',
  organisation_name: '',
  abn_acn: '',
});

type StaffOrderCustomerForm = ReturnType<typeof emptyForm>;

type ContactBaselineSnapshot = {
  customer_type: 'residential' | 'business';
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  service_address: string;
  billing_address: string;
  organisation_name: string;
  abn_acn: string;
};

function normStr(v: string | undefined | null): string {
  return String(v ?? '').trim();
}

function baselineFromAccount(acc: any): ContactBaselineSnapshot {
  const addr = normStr(acc?.address_formatted);
  return {
    customer_type: acc?.account_type === 'business' ? 'business' : 'residential',
    first_name: normStr(acc?.contact_first_name),
    last_name: normStr(acc?.contact_last_name),
    email: normStr(acc?.email),
    mobile_number: normStr(acc?.mobile_number || acc?.main_phone),
    service_address: addr,
    billing_address: addr,
    organisation_name: normStr(acc?.organisation_name),
    abn_acn: normStr(acc?.abn),
  };
}

function baselineFromForm(f: StaffOrderCustomerForm): ContactBaselineSnapshot {
  return {
    customer_type: f.customer_type,
    first_name: normStr(f.first_name),
    last_name: normStr(f.last_name),
    email: normStr(f.email),
    mobile_number: normStr(f.mobile_number),
    service_address: normStr(f.service_address),
    billing_address: normStr(f.billing_address),
    organisation_name: normStr(f.organisation_name),
    abn_acn: normStr(f.abn_acn),
  };
}

function contactBaselinesDiffer(
  current: ContactBaselineSnapshot,
  baseline: ContactBaselineSnapshot | null,
): boolean {
  if (!baseline) return false;
  const keys: (keyof ContactBaselineSnapshot)[] = [
    'customer_type',
    'first_name',
    'last_name',
    'email',
    'mobile_number',
    'service_address',
    'billing_address',
    'organisation_name',
    'abn_acn',
  ];
  return keys.some((k) => current[k] !== baseline[k]);
}

function formFromAccount(acc: any): StaffOrderCustomerForm {
  const addr = acc.address_formatted || '';
  return {
    customer_type: acc.account_type === 'business' ? 'business' : 'residential',
    first_name: acc.contact_first_name || '',
    last_name: acc.contact_last_name || '',
    email: acc.email || '',
    mobile_number: acc.mobile_number || acc.main_phone || '',
    service_address: addr,
    billing_address: addr,
    organisation_name: acc.organisation_name || '',
    abn_acn: acc.abn || '',
  };
}

function sanitizeContactMobile(raw: string): string | undefined {
  const t = raw.trim().replace(/\s/g, '');
  if (!t) return undefined;
  if (/^\+?[0-9]{8,20}$/.test(t)) return t;
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 8 && digits.length <= 20) return digits;
  return undefined;
}

/** Match backend StaffOrderCreate mobile normalization (VARCHAR 20, ≥8 digits). */
function normalizeStaffOrderMobileForSubmit(raw: string): string {
  let s = raw.trim();
  if (!s) return '';
  s = s.split(/[[\];]|(?:\bext\.?\s*\d*)|(?:\bx\s*\d+)/i)[0].trim();
  s = s.replace(/[^+0-9\s\-()]/g, '').trim();
  s = s.replace(/\s+/g, ' ');
  if (!s) return '';
  const compact = s.replace(/\s/g, '');
  const digitsOnly = compact.replace(/\D/g, '');
  if (digitsOnly.length < 8) return compact.slice(0, 20);
  if (compact.length <= 20) return compact;
  return digitsOnly.slice(0, 20);
}

function formatApiError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const d = e.response?.data?.detail;
  if (Array.isArray(d)) {
    return d
      .map((x: unknown) =>
        typeof x === 'string' ? x : (x as any)?.msg || (x as any)?.message || JSON.stringify(x),
      )
      .join(' ');
  }
  if (typeof d === 'string') return d;
  if (d != null && typeof d === 'object') return JSON.stringify(d);
  return e.message || 'Request failed';
}

function newRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function StaffNewOrderDialog({
  open,
  onClose,
  onSuccess,
  defaultAccountId,
  lockAccount,
  profileServiceAddress,
}: StaffNewOrderDialogProps) {
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [orderCategories, setOrderCategories] = useState<CategoryWithSubcategories[]>([]);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [cart, setCart] = useState<CartRow[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditContext, setWizardEditContext] = useState<{ rowId: string; line: StaffOrderCartLinePayload } | null>(
    null,
  );

  const [accountId, setAccountId] = useState('');
  const [accountLabel, setAccountLabel] = useState<string | null>(null);
  const [accountQuery, setAccountQuery] = useState('');
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountHydrating, setAccountHydrating] = useState(false);
  const [accountContactBaseline, setAccountContactBaseline] = useState<ContactBaselineSnapshot | null>(null);
  const [contactPromptOpen, setContactPromptOpen] = useState(false);
  const [contactSaveBusy, setContactSaveBusy] = useState(false);

  const lockedAccountId = (defaultAccountId || '').trim();

  const [leaveUnassigned, setLeaveUnassigned] = useState(false);

  const subcategorySlugById = useMemo(() => {
    const r: Record<string, string> = {};
    for (const c of orderCategories) {
      for (const s of c.subcategories || []) {
        r[s.id] = s.slug;
      }
    }
    return r;
  }, [orderCategories]);

  const cartTotals = useMemo(() => {
    let monthly = 0;
    let setup = 0;
    let units = 0;
    for (const row of cart) {
      const n = row.instances.length;
      units += n;
      const mp = row.product.monthly_price != null ? Number(row.product.monthly_price) : 0;
      const sp = row.product.setup_price != null ? Number(row.product.setup_price) : 0;
      monthly += mp * n;
      setup += sp * n;
    }
    return { monthly, setup, units };
  }, [cart]);

  useEffect(() => {
    if (!open) return;
    setDialogError(null);
    setLoadErr(null);
    setCart([]);
    setForm({ ...emptyForm(), service_address: profileServiceAddress ?? '' });
    setServiceTypeFilter('');
    setAccountQuery('');
    setAccountOptions([]);
    if (lockAccount && lockedAccountId) {
      setAccountId(lockedAccountId);
    } else {
      setAccountId('');
    }
    setAccountLabel(null);
    setLeaveUnassigned(false);
    setAccountContactBaseline(null);
    setContactPromptOpen(false);
    setAccountHydrating(false);

    (async () => {
      try {
        const [types, prods, cats] = await Promise.all([
          api.getServiceTypes(false),
          api.adminListProducts(),
          api.getOrderCategories(),
        ]);
        setServiceTypes(
          (types || [])
            .slice()
            .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
        );
        setOrderCategories(Array.isArray(cats) ? cats : []);
        const norm: OrderProduct[] = (prods || []).map((p: any) => ({
          ...p,
          monthly_price: p.monthly_price != null ? parseFloat(String(p.monthly_price)) : undefined,
          setup_price: p.setup_price != null ? parseFloat(String(p.setup_price)) : undefined,
          one_time_price: p.one_time_price != null ? parseFloat(String(p.one_time_price)) : undefined,
        }));
        setProducts(norm.filter((p) => p.is_active));
      } catch (e: unknown) {
        setLoadErr((e as Error)?.message || 'Failed to load service types or products');
        setProducts([]);
        setServiceTypes([]);
        setOrderCategories([]);
      }
    })();
  }, [open, lockAccount, lockedAccountId]);

  useEffect(() => {
    if (!open || !lockAccount || !lockedAccountId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const acc = await api.getAccount(lockedAccountId);
        if (cancelled || !acc) return;
        const label =
          acc.display_name ||
          [acc.contact_first_name, acc.contact_last_name].filter(Boolean).join(' ') ||
          acc.organisation_name ||
          acc.email ||
          lockedAccountId;
        setAccountLabel(String(label));
        setForm(formFromAccount(acc));
        setAccountContactBaseline(baselineFromAccount(acc));
      } catch {
        if (!cancelled) {
          setAccountLabel(lockedAccountId);
          setAccountContactBaseline(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, lockAccount, lockedAccountId]);

  useEffect(() => {
    if (!open || lockAccount) return;
    const q = accountQuery.trim();
    if (q.length < 2) {
      setAccountOptions([]);
      return;
    }
    const t = window.setTimeout(() => {
      setAccountLoading(true);
      api
        .getAccounts({ q, per_page: 30, page: 1 })
        .then((res) => {
          setAccountOptions(res?.accounts || []);
        })
        .catch(() => setAccountOptions([]))
        .finally(() => setAccountLoading(false));
    }, 350);
    return () => window.clearTimeout(t);
  }, [accountQuery, open, lockAccount]);

  const selectedAccountOption = useMemo(() => {
    if (!accountId) return null;
    return accountOptions.find((a) => a.id === accountId) || { id: accountId, display_name: accountLabel || accountId };
  }, [accountId, accountOptions, accountLabel]);

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setWizardEditContext(null);
  }, []);

  const handleWizardComplete = useCallback((payload: StaffOrderCartLinePayload, opts?: StaffOrderWizardCompleteOptions) => {
    // Reflect any price overrides from instance field values back onto the product so the
    // cart display and totals show the actual price the operator has set.
    const firstInst = payload.instances[0] ?? {};
    const instMonthly = firstInst.monthly_price;
    const instSetup = firstInst.setup_price;
    const effectiveMonthly = instMonthly != null && String(instMonthly).trim() !== ''
      ? parseFloat(String(instMonthly))
      : payload.product.monthly_price;
    const effectiveSetup = instSetup != null && String(instSetup).trim() !== ''
      ? parseFloat(String(instSetup))
      : payload.product.setup_price;
    // For Voice - Business SIP trunks, multiply the monthly price by the channel count.
    const sipChannels = isVoiceBusinessProduct(payload.product, subcategorySlugById)
      ? Math.max(1, parseInt(String(firstInst.sip_channels ?? '1'), 10) || 1)
      : 1;
    const product = { ...payload.product, monthly_price: (effectiveMonthly ?? 0) * sipChannels, setup_price: effectiveSetup };

    if (opts?.replaceRowId) {
      setCart((c) =>
        c.map((row) =>
          row.id === opts.replaceRowId
            ? { ...row, product, instances: payload.instances.map((i) => ({ ...i })), portingFiles: payload.portingFiles }
            : row,
        ),
      );
    } else {
      setCart((c) => [...c, { id: newRowId(), product, instances: payload.instances, portingFiles: payload.portingFiles }]);
    }

    // DID auto-inject: greedy packing — largest block first, then smaller blocks for remainder
    if (isVoiceProduct(payload.product) && !opts?.replaceRowId) {
      const totalDIDs = payload.instances.reduce((sum, inst) => {
        return sum + (parseInt(String(inst.did_count || '0'), 10) || 0);
      }, 0);
      // Inherit service address from the voice product so backend validation passes
      const voiceServiceAddress = String(payload.instances[0]?.service_address ?? '').trim();
      if (totalDIDs > 0) {
        void (async () => {
          try {
            const { mappings } = await api.getDidProductMappings();
            // mappings arrive sorted descending by block_size
            if (!mappings.length) {
              setDialogError(`Warning: No DID product mappings configured. Add the DID product manually.`);
              return;
            }
            // Build list of {product_id, qty} using greedy algorithm
            const lines: { product_id: string; qty: number }[] = [];
            let remaining = totalDIDs;
            for (const m of mappings) {
              if (remaining <= 0) break;
              const qty = Math.floor(remaining / m.block_size);
              if (qty > 0) {
                lines.push({ product_id: m.product_id, qty });
                remaining -= qty * m.block_size;
              }
            }
            if (remaining > 0) {
              setDialogError(
                `Warning: ${remaining} DID(s) could not be covered by configured block sizes. Add them manually.`,
              );
            }
            // Fetch all distinct products and inject cart lines
            const seen = new Set<string>();
            const distinctIds = lines.map((l) => l.product_id).filter((id) => seen.has(id) ? false : (seen.add(id), true));
            const productMap: Record<string, OrderProduct> = {};
            await Promise.all(
              distinctIds.map(async (pid) => {
                const p = await api.adminGetProduct(pid);
                productMap[pid] = {
                  ...p,
                  monthly_price: p.monthly_price != null ? parseFloat(String(p.monthly_price)) : undefined,
                  setup_price: p.setup_price != null ? parseFloat(String(p.setup_price)) : undefined,
                  one_time_price: p.one_time_price != null ? parseFloat(String(p.one_time_price)) : undefined,
                };
              }),
            );
            const newLines = lines
              .filter((l) => productMap[l.product_id])
              .map((l) => ({
                id: newRowId(),
                product: productMap[l.product_id],
                // One instance with quantity field — backend applies the multiplier
                instances: [{ ...(voiceServiceAddress ? { service_address: voiceServiceAddress } : {}), quantity: String(l.qty) }],
                portingFiles: [null],
                skipAddressValidation: true,
              }));
            if (newLines.length) {
              setCart((c) => [...c, ...newLines]);
            }
          } catch {
            setDialogError(
              `Warning: Could not auto-inject DID product(s) for ${totalDIDs} DID(s). Add them manually.`,
            );
          }
        })();
      }
    }
  }, [subcategorySlugById]);

  const executeCreateOrder = useCallback(async () => {
    const acc = lockAccount ? lockedAccountId : leaveUnassigned ? '' : accountId.trim();
    const useLineAddresses = cartHasLineServiceAddressRequirement(cart, subcategorySlugById);
    const payload: Record<string, unknown> = {
      lines: cart.map((row) => ({
        product_id: row.product.id,
        instances: row.instances,
      })),
      customer_type: form.customer_type,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      mobile_number: normalizeStaffOrderMobileForSubmit(form.mobile_number),
      service_address: useLineAddresses
        ? deriveOrderLevelServiceAddressFromCart(cart, form.service_address, subcategorySlugById)
        : form.service_address.trim(),
    };
    if (acc) payload.account_id = acc;
    const bill = form.billing_address.trim();
    if (bill) payload.billing_address = bill;
    if (form.customer_type === 'business') {
      payload.organisation_name = form.organisation_name.trim();
      payload.abn_acn = form.abn_acn.trim();
    }
    const order = await api.adminCreateStaffOrder(payload as any);

    // Upload any porting authority documents (port-in mobile instances).
    for (let lineIdx = 0; lineIdx < cart.length; lineIdx++) {
      const files = cart[lineIdx].portingFiles;
      if (!files) continue;
      for (let instIdx = 0; instIdx < files.length; instIdx++) {
        const file = files[instIdx];
        if (!file) continue;
        try {
          await api.adminUploadOrderItemPortingDocument(
            order.id,
            lineIdx + 1,
            instIdx + 1,
            file,
          );
        } catch {
          // Document upload is best-effort; order is already created.
        }
      }
    }

    onSuccess?.();
    onClose();
  }, [
    lockAccount,
    lockedAccountId,
    leaveUnassigned,
    accountId,
    cart,
    form,
    subcategorySlugById,
    onClose,
    onSuccess,
  ]);

  const handleSubmit = async () => {
    setDialogError(null);
    if (cart.length === 0) {
      setDialogError('Add at least one catalog product to the order.');
      return;
    }
    if (!lockAccount && !leaveUnassigned && !accountId.trim()) {
      setDialogError('Select a CRM account, or tick "Leave unassigned" to queue without an account.');
      return;
    }
    const mobDigits = normalizeStaffOrderMobileForSubmit(form.mobile_number).replace(/\D/g, '');
    if (mobDigits.length < 8) {
      setDialogError('Enter a valid mobile number (at least 8 digits).');
      return;
    }
    const addrMsg = validateCartInstanceServiceAddresses(cart, subcategorySlugById);
    if (addrMsg) {
      setDialogError(addrMsg);
      return;
    }
    if (!cartHasLineServiceAddressRequirement(cart, subcategorySlugById) && form.service_address.trim().length < 3) {
      setDialogError('Enter a service address (at least 3 characters).');
      return;
    }
    const acc = lockAccount ? lockedAccountId : leaveUnassigned ? '' : accountId.trim();
    if (acc && contactBaselinesDiffer(baselineFromForm(form), accountContactBaseline)) {
      setContactPromptOpen(true);
      return;
    }
    try {
      setSaving(true);
      await executeCreateOrder();
    } catch (err: any) {
      setDialogError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleContactPromptAddAndCreate = async () => {
    const acc = lockAccount ? lockedAccountId : accountId.trim();
    if (!acc) {
      setContactPromptOpen(false);
      return;
    }
    setContactSaveBusy(true);
    setDialogError(null);
    try {
      const mobile = sanitizeContactMobile(form.mobile_number.trim());
      await api.createAccountContact(acc, {
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
        email: form.email.trim() || undefined,
        mobile_number: mobile,
        contact_type: 'secondary',
      });
      setAccountContactBaseline(baselineFromForm(form));
      setContactPromptOpen(false);
      setSaving(true);
      await executeCreateOrder();
    } catch (err: any) {
      setDialogError(formatApiError(err));
    } finally {
      setContactSaveBusy(false);
      setSaving(false);
    }
  };

  const handleContactPromptCreateOnly = async () => {
    setContactPromptOpen(false);
    try {
      setSaving(true);
      await executeCreateOrder();
    } catch (err: any) {
      setDialogError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StaffOrderAddProductWizard
        open={wizardOpen}
        onClose={closeWizard}
        products={products}
        serviceTypes={serviceTypes}
        serviceTypeFilter={serviceTypeFilter}
        onServiceTypeFilterChange={setServiceTypeFilter}
        subcategorySlugById={subcategorySlugById}
        profileServiceAddress={normStr(form.service_address)}
        editContext={wizardEditContext}
        onComplete={handleWizardComplete}
      />
      <Dialog
        open={open}
        onClose={() => {
          if (saving || contactSaveBusy) return;
          setContactPromptOpen(false);
          onClose();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Build a cart of catalog products. Each unit (quantity) is configured with the fields defined for that
            product&apos;s category. No payment is stored in SonOps (external billing).
          </Typography>
          {loadErr && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loadErr}
            </Alert>
          )}
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              {lockAccount && lockedAccountId ? (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    CRM account
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {accountLabel || lockedAccountId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Order will be linked to this account.
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  <FormControlLabel
                    sx={{ mb: 1, display: 'block' }}
                    control={
                      <Checkbox
                        checked={leaveUnassigned}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setLeaveUnassigned(on);
                          if (on) {
                            setAccountId('');
                            setAccountLabel(null);
                            setAccountQuery('');
                            setAccountOptions([]);
                            setAccountContactBaseline(null);
                          }
                        }}
                      />
                    }
                    label="Leave unassigned (new customer queue - no CRM account)"
                  />
                  <Autocomplete
                    disabled={leaveUnassigned}
                    options={accountOptions}
                    loading={accountLoading}
                    filterOptions={(x) => x}
                    getOptionLabel={(option: any) =>
                      option?.display_name ||
                      [option?.contact_first_name, option?.contact_last_name].filter(Boolean).join(' ') ||
                      option?.organisation_name ||
                      option?.email ||
                      option?.id ||
                      ''
                    }
                    value={leaveUnassigned ? null : (selectedAccountOption as any)}
                    onChange={(_e, value: any) => {
                      if (!value?.id) {
                        setAccountId('');
                        setAccountLabel(null);
                        setAccountContactBaseline(null);
                        return;
                      }
                      setLeaveUnassigned(false);
                      const id = String(value.id);
                      setAccountId(id);
                      setAccountLabel(
                        value.display_name ||
                          [value.contact_first_name, value.contact_last_name].filter(Boolean).join(' ') ||
                          value.organisation_name ||
                          value.email ||
                          id,
                      );
                      void (async () => {
                        setAccountHydrating(true);
                        setDialogError(null);
                        try {
                          const acc = await api.getAccount(id);
                          setForm(formFromAccount(acc));
                          const label =
                            acc.display_name ||
                            [acc.contact_first_name, acc.contact_last_name].filter(Boolean).join(' ') ||
                            acc.organisation_name ||
                            acc.email ||
                            id;
                          setAccountLabel(String(label));
                          setAccountContactBaseline(baselineFromAccount(acc));
                        } catch {
                          setForm(formFromAccount(value));
                          setAccountContactBaseline(baselineFromAccount(value));
                        } finally {
                          setAccountHydrating(false);
                        }
                      })();
                    }}
                    onInputChange={(_e, v) => setAccountQuery(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="CRM account"
                        required={!leaveUnassigned}
                        helperText={
                          accountHydrating
                            ? 'Loading account details…'
                            : leaveUnassigned
                              ? 'Order will not be linked to a CRM account.'
                              : 'Type at least 2 characters to search, or use unassigned above.'
                        }
                        placeholder="Search accounts…"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {accountHydrating ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Cart
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setWizardEditContext(null);
                    setWizardOpen(true);
                  }}
                  disabled={!!loadErr}
                >
                  Add catalog product
                </Button>
              </Box>
              {cart.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No products yet. Use <strong>Add catalog product</strong> to add lines (multiple products and
                  quantities supported).
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Monthly</TableCell>
                        <TableCell align="right">Setup</TableCell>
                        <TableCell align="right" width={100}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cart.map((row) => {
                        const n = row.instances.length;
                        const mp = row.product.monthly_price != null ? Number(row.product.monthly_price) : 0;
                        const sp = row.product.setup_price != null ? Number(row.product.setup_price) : 0;
                        const sipCh = isVoiceBusinessProduct(row.product, subcategorySlugById)
                          ? Math.max(1, parseInt(String(row.instances[0]?.sip_channels ?? '1'), 10) || 1)
                          : null;
                        const unitMp = sipCh != null && sipCh > 0 ? mp / sipCh : null;
                        return (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {row.product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.product.service_type_code || row.product.product_type}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{n}</TableCell>
                            <TableCell align="right">
                              {sipCh != null && unitMp != null ? (
                                <Box>
                                  <Typography variant="body2">${(mp * n).toFixed(2)}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    ${unitMp.toFixed(2)} × {sipCh} ch
                                  </Typography>
                                </Box>
                              ) : (
                                `$${(mp * n).toFixed(2)}`
                              )}
                            </TableCell>
                            <TableCell align="right">${(sp * n).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                aria-label="Edit line"
                                onClick={() => {
                                  setWizardEditContext({
                                    rowId: row.id,
                                    line: {
                                      product: row.product,
                                      instances: row.instances.map((i) => ({ ...i })),
                                    },
                                  });
                                  setWizardOpen(true);
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Remove"
                                onClick={() => setCart((c) => c.filter((x) => x.id !== row.id))}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Totals</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {cartTotals.units}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${cartTotals.monthly.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${cartTotals.setup.toFixed(2)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              )}
              {cart.length > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                  Totals use catalog monthly and setup prices per unit. Final order totals may include one-time items
                  from the server.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600}>
                Customer details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {leaveUnassigned
                  ? 'Enter the customer this order is for (required to create the order).'
                  : accountId || lockAccount
                    ? 'Pre-filled from the CRM account when linked. Edit if this order should go to an alternate contact.'
                    : 'Select a CRM account above to auto-fill, or enter details manually.'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer type</InputLabel>
                <Select
                  label="Customer type"
                  value={form.customer_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customer_type: e.target.value as 'residential' | 'business' }))
                  }
                >
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                label="First name"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                label="Last name"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Mobile number"
                value={form.mobile_number}
                onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                minRows={2}
                label="Service address"
                value={form.service_address}
                onChange={(e) => setForm((f) => ({ ...f, service_address: e.target.value }))}
                helperText={
                  cartHasLineServiceAddressRequirement(cart, subcategorySlugById)
                    ? 'Per-line addresses for NBN, Supa Networks, Mobile, and Voice are set when you add or edit those products.'
                    : undefined
                }
                sx={{ display: cartHasLineServiceAddressRequirement(cart, subcategorySlugById) ? 'none' : undefined }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Billing address (optional)"
                value={form.billing_address}
                onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value }))}
              />
            </Grid>
            {form.customer_type === 'business' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Organisation name"
                    value={form.organisation_name}
                    onChange={(e) => setForm((f) => ({ ...f, organisation_name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="ABN / ACN"
                    value={form.abn_acn}
                    onChange={(e) => setForm((f) => ({ ...f, abn_acn: e.target.value }))}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !saving && !contactSaveBusy && onClose()} disabled={saving || contactSaveBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving || contactSaveBusy}>
            {saving ? 'Creating…' : 'Create order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactPromptOpen}
        onClose={() => !contactSaveBusy && !saving && setContactPromptOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save as account contact?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Customer details on this order no longer match the CRM account we loaded. You can still create the
              order as entered.
            </Typography>
            <Typography variant="body2">
              Should we add <strong>{[form.first_name, form.last_name].filter(Boolean).join(' ') || 'this person'}</strong>{' '}
              as an <strong>additional (secondary) contact</strong> on the account before creating the order?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactPromptOpen(false)} disabled={contactSaveBusy || saving}>
            Go back
          </Button>
          <Button onClick={() => void handleContactPromptCreateOnly()} disabled={contactSaveBusy || saving}>
            Create order only
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleContactPromptAddAndCreate()}
            disabled={contactSaveBusy || saving}
          >
            {contactSaveBusy ? 'Saving…' : 'Add contact & create order'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default StaffNewOrderDialog;
